import React from 'react';
import { Phone, MessageSquare, Mail, Chrome, Eye, EyeOff, Fingerprint, Facebook, Lock, User, ArrowRight } from 'lucide-react';
import '../../styles/Auth.css';

const PhoneLoginModal = ({
  mobile,
  setMobile,
  password,
  checkPasswordStrength,
  passwordStrength,
  showPassword,
  setShowPassword,
  sendOTPForLogin,
  loginMethod,
  setLoginMethod,
  handleEmailLogin,
  rememberMe,
  setRememberMe,
  handleSocialLogin,
  biometricEnabled,
  handleBiometricLogin,
  socialMediaLoginEnabled,
  showForgotPassword,
  showSignUp,
  onShowAbout,
  error,
  setError,
  mobileNotFound,
  setMobileNotFound
}) => {
  const logoSrc = `${process.env.PUBLIC_URL || ''}/logo.png`;

  return (
    <div className="signup-container">
      <div className="signup-card">
        <div className="signup-header">
          <div className="logo">
            <img src={logoSrc} alt="Dealstore Logo" className="w-8 h-8" />
          </div>
          <h1>Welcome Back</h1>
          <p className="subtitle">Sign in to access fresh dairy delivered daily</p>
        </div>

        {/* Biometric Login Option */}
        {biometricEnabled && (
          <div className="mb-6">
            <button
              onClick={handleBiometricLogin}
              className="w-full bg-gradient-to-r from-purple-600 via-purple-500 to-pink-500 text-white py-4 rounded-2xl font-semibold flex items-center justify-center gap-3 hover:shadow-2xl hover:shadow-purple-500/50 transition-all transform hover:scale-[1.02] active:scale-[0.98] group"
            >
              <Fingerprint size={24} className="group-hover:rotate-12 transition-transform" />
              <span>Sign in with Biometrics</span>
              <ArrowRight size={20} className="group-hover:translate-x-1 transition-transform" />
            </button>
            <div className="divider">
              <span>OR CONTINUE WITH</span>
            </div>
          </div>
        )}

        {loginMethod === 'phone' && (
          <>
            <div className="form-group">
              <label htmlFor="mobile">Mobile Number</label>
              <div className="input-wrapper">
                <input
                  id="mobile"
                  type="tel"
                  maxLength="10"
                  value={mobile}
                  onChange={(e) => {
                    const value = e.target.value.replace(/\D/g, '');
                    setMobile(value);
                    if (error && setError) setError('');
                    if (mobileNotFound && setMobileNotFound) setMobileNotFound(false);
                  }}
                  placeholder="Enter your mobile number"
                  autoFocus
                  className={mobile.length === 10 ? 'success' : ''}
                />
              </div>
            </div>
            
            {/* Error Message */}
            {error && (
              <div className={`rounded-2xl overflow-hidden border ${mobileNotFound ? 'border-orange-200' : 'border-red-200'}`}>
                {/* Error row */}
                <div className={`flex items-start gap-2.5 px-4 py-3 ${mobileNotFound ? 'bg-orange-50' : 'bg-red-50'}`}>
                  <svg className={`w-5 h-5 flex-shrink-0 mt-0.5 ${mobileNotFound ? 'text-orange-500' : 'text-red-500'}`} fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                  </svg>
                  <span className={`text-sm font-medium ${mobileNotFound ? 'text-orange-700' : 'text-red-700'}`}>{error}</span>
                </div>

                {/* Create account CTA — only when mobile not found */}
                {mobileNotFound && (
                  <div className="px-4 py-3 bg-gradient-to-r from-orange-500 to-pink-500 flex items-center justify-between gap-3">
                    <div>
                      <p className="text-white text-xs font-bold leading-tight">New to DealStore?</p>
                      <p className="text-white/75 text-[11px]">Create a free account in seconds</p>
                    </div>
                    <button
                      onClick={showSignUp}
                      className="flex-shrink-0 bg-white text-orange-600 text-xs font-bold px-4 py-2 rounded-xl shadow-sm hover:shadow-md active:scale-95 transition-all whitespace-nowrap"
                    >
                      Create Account →
                    </button>
                  </div>
                )}
              </div>
            )}
            {/* Password Input and OTP Buttons 
            <div className="mb-4 sm:mb-6">
              <label className="block text-xs sm:text-sm font-semibold text-gray-700 mb-2">
                Password
              </label>
              <div className="relative">
                <input
                  type={showPassword ? "text" : "password"}
                  placeholder="Enter your password"
                  value={password}
                  onChange={(e) => checkPasswordStrength(e.target.value)}
                  className="w-full border-2 border-gray-200 rounded-xl px-3 py-2.5 sm:px-4 sm:py-3 pr-10 sm:pr-12 outline-none focus:border-blue-500 transition-all text-sm sm:text-base"
                  id="password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 sm:right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  {showPassword ? <EyeOff size={18} className="sm:w-5 sm:h-5" /> : <Eye size={18} className="sm:w-5 sm:h-5" />}
                </button>
              </div>
              
              {password && passwordStrength.score > 0 && (
                <div className="mt-2">
                  <div className="flex gap-1 mb-1">
                    {[1, 2, 3, 4, 5].map((level) => (
                      <div
                        key={level}
                        className={`h-1 flex-1 rounded-full transition-all ${
                          level <= passwordStrength.score
                            ? passwordStrength.color
                            : 'bg-gray-200'
                        }`}
                      />
                    ))}
                  </div>
                  <p className="text-xs text-gray-600">
                    Password strength: <span className="font-semibold">{passwordStrength.label}</span>
                  </p>
                </div>
              )}
              
              <button 
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  showForgotPassword();
                }}
                className="text-xs sm:text-sm text-blue-600 hover:underline mt-2"
              >
                Forgot Password?
              </button>
            </div>
            */}

            <div className="space-y-3 mb-6">
              {['sms', 'both'].includes(process.env.REACT_APP_OTP_CHANNEL || 'sms') && (
                <button
                  onClick={() => sendOTPForLogin('sms')}
                  disabled={mobile.length !== 10}
                  className="w-full bg-gradient-to-r from-blue-600 to-blue-700 text-white py-4 rounded-2xl font-semibold flex items-center justify-center gap-3 hover:shadow-2xl hover:shadow-blue-500/50 transition-all disabled:from-gray-300 disabled:to-gray-400 disabled:cursor-not-allowed disabled:shadow-none transform hover:scale-[1.02] active:scale-[0.98] group relative overflow-hidden"
                >
                  <div className="absolute inset-0 bg-white/20 translate-y-full group-hover:translate-y-0 transition-transform duration-300"></div>
                  <MessageSquare size={20} className="relative z-10" />
                  <span className="relative z-10">Send OTP via SMS</span>
                  <ArrowRight size={20} className="relative z-10 group-hover:translate-x-1 transition-transform" />
                </button>
              )}

              {['whatsapp', 'both'].includes(process.env.REACT_APP_OTP_CHANNEL || 'sms') && (
                <button
                  onClick={() => sendOTPForLogin('whatsapp')}
                  disabled={mobile.length !== 10}
                  className="w-full bg-gradient-to-r from-green-600 to-green-700 text-white py-4 rounded-2xl font-semibold flex items-center justify-center gap-3 hover:shadow-2xl hover:shadow-green-500/50 transition-all disabled:from-gray-300 disabled:to-gray-400 disabled:cursor-not-allowed disabled:shadow-none transform hover:scale-[1.02] active:scale-[0.98] group relative overflow-hidden"
                >
                  <div className="absolute inset-0 bg-white/20 translate-y-full group-hover:translate-y-0 transition-transform duration-300"></div>
                  <MessageSquare size={20} className="relative z-10" />
                  <span className="relative z-10">Send OTP via WhatsApp</span>
                  <ArrowRight size={20} className="relative z-10 group-hover:translate-x-1 transition-transform" />
                </button>
              )}
            </div>
          </>
        )}

        {loginMethod === 'email' && (
          <>
            <div className="form-group">
              <label>
                Email Address
              </label>
              <div className="input-wrapper">
                <input
                  type="email"
                  placeholder="your.email@example.com"
                  id="email-input"
                />
              </div>
            </div>

            <div className="form-group">
              <label>
                Password
              </label>
              <div className="input-wrapper">
                <input
                  type={showPassword ? "text" : "password"}
                  placeholder="Enter your password"
                  value={password}
                  onChange={(e) => checkPasswordStrength(e.target.value)}
                  id="password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="password-toggle"
                >
                  {showPassword ? <EyeOff size={18} className="sm:w-5 sm:h-5" /> : <Eye size={18} className="sm:w-5 sm:h-5" />}
                </button>
              </div>
              
              <button 
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  showForgotPassword();
                }}
                className="link-button"
              >
                Forgot Password?
              </button>
            </div>

            <button
              onClick={() => {
                const email = document.getElementById('email-input').value;
                const pwd = document.getElementById('password-input').value;
                handleEmailLogin(email, pwd);
              }}
              className="submit-btn"
            >
              Sign In with Email
            </button>

            <div className="checkbox-group mb-3 sm:mb-4">
              <input
                type="checkbox"
                id="remember-me"
                checked={rememberMe}
                onChange={(e) => setRememberMe(e.target.checked)}
              />
              <label htmlFor="remember-me" className="checkbox-label">
                Remember me on this device
              </label>
            </div>
          </>
        )}

        <div className="divider">
          <span>OR</span>
        </div>
        {socialMediaLoginEnabled && (
        <div className="social-login">
          <button
            onClick={() => handleSocialLogin('Google')}
            className="social-btn"
          >
            <Chrome size={20} className="text-blue-600 group-hover:rotate-12 transition-transform" />
            <span>Google</span>
          </button>

          <button
            onClick={() => handleSocialLogin('Facebook')}
            className="social-btn"
          >
            <Facebook size={20} className="text-blue-600 group-hover:rotate-12 transition-transform" fill="currentColor" />
            <span>Facebook</span>
          </button>
        </div>
        )}
        <div className="flex gap-2 justify-center mb-4">
          <button
            onClick={() => setLoginMethod('phone')}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
              loginMethod === 'phone'
                ? 'bg-gradient-to-r from-blue-500 to-blue-600 text-white shadow-lg shadow-blue-500/30'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            <Phone size={16} className="inline mr-1" />
            Phone
          </button>
          <button
            onClick={() => setLoginMethod('email')}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
              loginMethod === 'email'
                ? 'bg-gradient-to-r from-blue-500 to-blue-600 text-white shadow-lg shadow-blue-500/30'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            <Mail size={16} className="inline mr-1" />
            Email
          </button>
        </div>

        <div className="footer mb-4">
        <p>
          By continuing, you agree to our{' '}
          <button className="link-button" onClick={() => window.setActiveTab && window.setActiveTab('terms')}>Terms</button>
          {' '}and{' '}
          <button className="link-button" onClick={() => window.setActiveTab && window.setActiveTab('privacy')}>Privacy Policy</button>
        </p>
        </div>

        <div className="footer pt-4 border-t border-gray-200">
          <p>
            Don't have an account?{' '}
            <button 
              type="button"
              onClick={(e) => {
                e.preventDefault();
                showSignUp();
              }}
              className="link-button inline-flex items-center gap-1 group"
            >
              <span>Create Account</span>
              <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
            </button>
          </p>
          <p className="text-xs text-gray-400 mt-4">Protected by enterprise-grade security 🔒</p>
          {onShowAbout && (
            <p className="text-xs text-gray-400 mt-2">
              <button onClick={onShowAbout} className="text-blue-500 hover:underline">About Us</button>
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default PhoneLoginModal;